import React, { useState, useEffect } from 'react';
import { Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Box } from '@mui/material';
import { FetchWithAuth, ExerciseSummary } from '../../types';
import { API_URL } from '../../utils';
import { useLanguage } from '../../i18n/LanguageContext';
import { WorkoutStatsCard } from '../workouts/WorkoutStatsCard';

interface ExerciseSummaryViewProps {
  fetchWithAuth: FetchWithAuth;
  onServiceUnavailable?: () => void;
}

export const ExerciseSummaryView: React.FC<ExerciseSummaryViewProps> = ({ fetchWithAuth, onServiceUnavailable }) => {
  const [summaries, setSummaries] = useState<ExerciseSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/exercises?include=summary`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setSummaries(data);
        } else {
          setSummaries([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching exercise summary:', err);
        if (err.isServiceUnavailable && onServiceUnavailable) {
          onServiceUnavailable();
        } else {
          setError(t('summary.error'));
        }
        setLoading(false);
      });
  }, [fetchWithAuth, t, onServiceUnavailable]);

  if (loading) return <Typography>{t('summary.loading')}</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        {t('summary.title')}
      </Typography>
      
      {/* Workout Statistics Card */}
      <WorkoutStatsCard onServiceUnavailable={onServiceUnavailable} />
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('summary.exercise')}</TableCell>
              <TableCell align="center">{t('summary.lastTrained')}</TableCell>
              <TableCell align="center">{t('summary.maxWeight')}</TableCell>
              <TableCell align="center">{t('summary.prDate')}</TableCell>
              <TableCell align="center">{t('summary.prDetails')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summaries.map((summary) => (
              <TableRow key={summary.id}>
                <TableCell>
                  <Typography variant="body1" fontWeight="medium">
                    {summary.name}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {summary.lastTrained ? new Date(summary.lastTrained).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {summary.maxWeight !== null ? `${summary.maxWeight} kg` : '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {summary.prDate ? new Date(summary.prDate).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell align="center">
                  {summary.prSeries !== null && summary.prReps !== null ? (
                    <Typography variant="body2" color="text.secondary">
                      {summary.prSeries} × {summary.prReps}
                    </Typography>
                  ) : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}; 